// PEGA ESTE CÓDIGO COMPLETO EN src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthUser, setAuthUser } from '../utils/storage';
import { api } from '../utils/api';
import { AuthUser } from '../types';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string, hash?: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedUser = getAuthUser();
      if (storedUser) {
        setUser(storedUser);
      }
    } catch {
      // ignorar errores
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (username: string, password: string, hash?: string): Promise<boolean> => {
    // Lógica de login para admin
    if (!hash && username === 'admin' && password === 'Lisa24**') {
      const adminUser: AuthUser = { id: 'admin', role: 'admin', username: 'admin' };
      setUser(adminUser);
      setAuthUser(adminUser);
      return true;
    }

    // Lógica de login para cliente
    if (hash) {
      try {
        const clients = await api.getClients();
        const client = clients.find(
          (c) => c.hash === hash && c.username === username && c.password === password
        );
        if (client) {
          const clientUser: AuthUser = {
            id: client.id,
            role: 'client',
            username: client.username,
            clientHash: client.hash,
          };
          setUser(clientUser);
          setAuthUser(clientUser);

          // --- [NUEVO] Registramos el inicio de sesión en el log ---
          // Lo envolvemos en un try/catch para que, si falla, el login no se interrumpa.
          try {
            await api.logLoginSuccess({ clientId: client.id, clientName: client.name });
          } catch (logError) {
            console.error("Fallo al registrar el log de inicio de sesión:", logError);
          }
          
          return true;
        }
      } catch (err) {
        console.error('Error durante login de cliente:', err);
      }
    }

    return false;
  };

  const logout = () => {
    setUser(null);
    setAuthUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !loading && user !== null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
